import * as React from "react";
import * as ReactDOM from "react-dom";
import * as utils from "../../utils/utils";
import { EventManager } from "./globals";
import { PopupAlign, PopupTrigger } from "./";

type ChildrenFunc = (dataSource: any) => JSX.Element | JSX.Element[];

interface IProperties
{
    children?: JSX.Element | JSX.Element[] | ChildrenFunc,
    id: string;
    align?: PopupAlign;
    fitToTargetWidth?: boolean;
    offsetTop?: number;
    offsetLeft?: number;
    hasShadow?: boolean;
}

interface IState
{
    /**
     * Obere Position des Popups.
     */
    top: number,

    /**
     * Linke Position des Popups.
     */
    left: number,

    /**
     * Die Breite des Popups.
     */
    width: number,

    /**
     * Die Höhe des Popups.
     */
    height: number,

    /**
     * Angabe, ob das Popup sichtbar ist.
     */
    isVisible: boolean,

    /**
     * Enthält den angehängten Datensatz vom PopupProvider.
     */
    dataSource?: any
}

export class Popup extends React.Component<IProperties, IState>
{
    private _token: number;
    private _menuRef: HTMLElement;
    private _hideFunc: () => void;
    private _abort: boolean;

    public static defaultProps: Partial<IProperties> = {
        align: PopupAlign.mouseCursor,
        offsetTop: 2,
        offsetLeft: 2,
        hasShadow: true
    };

    constructor(props: IProperties)
    {
        super(props);

        this._hideFunc = this.hide.bind(this);
        this.state = {
            top: 0,
            left: 0,
            width: 0,
            height: 0,
            isVisible: false
        };
    }

    componentDidMount()
    {
        EventManager.on(`show:${this.props.id}`, (e, trigger, triggerDelay, dataSource) => this.show(e, trigger, triggerDelay, dataSource));
        EventManager.on(`hideOnMouseLeave:${this.props.id}`, this.hideOnMouseLeave);
        this._token = EventManager.on("hide", this._hideFunc);
    }

    componentWillUnmount()
    {
        EventManager.off(`show:${this.props.id}`);
        EventManager.off(`hideOnMouseLeave:${this.props.id}`);
        EventManager.off("hide", this._token);
    }

    private bindWindowEvent()
    {
        window.addEventListener("resize", this._hideFunc);
        window.addEventListener("contextmenu", this._hideFunc);
        window.addEventListener("mousedown", this._hideFunc);
        window.addEventListener("click", this._hideFunc);
        window.addEventListener("scroll", this._hideFunc);
    }

    private unBindWindowEvent()
    {
        window.removeEventListener("resize", this._hideFunc);
        window.removeEventListener("contextmenu", this._hideFunc);
        window.removeEventListener("mousedown", this._hideFunc);
        window.removeEventListener("click", this._hideFunc);
        window.removeEventListener("scroll", this._hideFunc);
    }

    private onMouseEnter = () =>
    {
        window.removeEventListener("contextmenu", this._hideFunc);
        window.removeEventListener("mousedown", this._hideFunc);
        window.removeEventListener("click", this._hideFunc);
    }

    private onMouseLeave = () =>
    {
        window.addEventListener("contextmenu", this._hideFunc);
        window.addEventListener("mousedown", this._hideFunc);
        window.addEventListener("click", this._hideFunc);
    }

    private setRef = (ref: HTMLElement) =>
    {
        this._menuRef = ref;

        if (ref != null)
        {
            // Ohne diesen Hick Hack werden die Events vom Browser nicht rechtzeitig erkannt.
            // Dadurch wird also verhindert, das man zu schnell auf eine Schaltfläche klickt und diese nicht ausgelöst wird.
            setTimeout(() => $(ref).css("visibility", ""), 150);
        }
    }

    private setMenuPosition(trigger: PopupTrigger, e: React.MouseEvent<HTMLElement>)
    {
        if (this._menuRef == null)
            return;

        const browserSize = {
            width: window.innerWidth,
            height: window.innerHeight
        };

        const menuSize = {
            width: this._menuRef.offsetWidth,
            height: this._menuRef.offsetHeight
        };

        let { top, left, width, height } = this.state;

        if (trigger === PopupTrigger.RightClick)
        {
            if ((top + menuSize.height) > browserSize.height)
            {
                top -= ((top + menuSize.height) - browserSize.height);
            }

            if ((left + menuSize.width) > browserSize.width)
            {
                left -= ((left + menuSize.width) - browserSize.width);
            }

            top += 1;
            left += 1;
        }
        else
        {
            const ref: HTMLElement = $(e.target).closest("[data-popup-provider]").get(0) as HTMLElement;
            if (ref != null)
            {
                const refInfo = {
                    width: ref.offsetWidth,
                    height: ref.offsetHeight,
                    top: ref.getBoundingClientRect().top,
                    left: ref.getBoundingClientRect().left
                };

                switch (this.props.align)
                {
                    case PopupAlign.targetNodeLeftTop:
                        {
                            top = refInfo.top;
                            left = refInfo.left + refInfo.width + this.props.offsetLeft;
                        }
                        break;
                    case PopupAlign.targetNodeBottomRight:
                        {
                            top = refInfo.top + refInfo.height + this.props.offsetTop;
                            left = refInfo.left + refInfo.width - menuSize.width;
                        }
                        break;
                    default:
                        {
                            top = refInfo.top + refInfo.height + this.props.offsetTop;
                            left = refInfo.left;
                        }
                        break;
                }

                if (this.props.fitToTargetWidth && menuSize.width < refInfo.width)
                {
                    width = refInfo.width;
                }

                height = menuSize.height;

                if (browserSize.height < (menuSize.height + top))
                {
                    top = browserSize.height - menuSize.height;
                }

                if (browserSize.width < (menuSize.width + left))
                {
                    left = browserSize.width - menuSize.width;
                }
            }
        }

        this.setState({
            top: top,
            left: left,
            width: width,
            height: height
        }, () => setTimeout(() => this.bindWindowEvent(), 100));
    }

    private show(e: React.MouseEvent<HTMLElement>, trigger: PopupTrigger, triggerDelay: number, dataSource: any)
    {
        if (this._abort)
            return;

        var mousePos = utils.getMousePosition(e.nativeEvent);
        EventManager.emit("hide");

        this.setState({
            top: mousePos.y,
            left: mousePos.x,
            isVisible: true,
            dataSource: dataSource
        }, () => this.setMenuPosition(trigger, e));
    }

    private abort(e: MouseEvent, trigger: PopupTrigger)
    {
        this._abort = trigger === PopupTrigger.Hover;
    }

    private hide()
    {
        var eventName: string = `hide:${this.props.id}`;
        var token: number;
        var returnValue: any;
        if (EventManager.has(eventName))
        {
            token = EventManager.getToken(eventName);
            returnValue = EventManager.emit(`hide:${this.props.id}`);
        }

        if (returnValue != null && returnValue[token] === false)
            return;

        this.unBindWindowEvent();
        this.setState({
            isVisible: false,
            width: 0,
            height: 0
        });
    }

    private hideOnMouseLeave = () =>
    {
        this.unBindWindowEvent();
        this.setState({
            isVisible: false,
            width: 0,
            height: 0
        });
    }

    render()
    {
        const { children, hasShadow } = this.props
        const { top, left, width, height, isVisible, dataSource } = this.state

        if (!isVisible)
            return null;

        var className: string[] = ["popup"]

        if (!hasShadow)
            className.push("popup--noshadow");

        return (
            <div
                className={className.join(" ")}
                ref={this.setRef}
                style={{
                    top: top,
                    left: left,
                    minWidth: width,
                    minHeight: height,
                    visibility: "hidden"
                }}
                onMouseEnter={this.onMouseEnter}
                onMouseLeave={this.onMouseLeave}>
                {typeof children === "function" ? children(dataSource) : children}
            </div>
        );
    }
}